"use client"
import { Link, useLocation } from "react-router-dom"
import styled from "styled-components"
import { motion } from "framer-motion"
import {
  Home as HomeIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material"
import SynthlinxLogo from "./SynthlinxLogo"

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: rgba(13, 12, 29, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border);
  padding: 0 20px;
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
`

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-text);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    color: var(--primary-text);
  }
`

const NavLinks = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`

const NavLink = styled(motion(Link))`
  color: var(--secondary-text);
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &.active {
    color: var(--accent-blue);
    background-color: rgba(79, 141, 251, 0.1);
  }
  
  &:hover {
    color: var(--highlight);
    background-color: rgba(79, 141, 251, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 5px;
    
    span {
      display: none;
    }
  }
`

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`

const UserInfo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-text);
  background-color: rgba(79, 141, 251, 0.1);
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid rgba(79, 141, 251, 0.2);
  
  @media (max-width: 768px) {
    span {
      display: none;
    }
  }
`

const LogoutButton = styled(motion.button)`
  background: none;
  border: none;
  color: var(--secondary-text);
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--highlight);
    background-color: rgba(79, 141, 251, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 5px;
    
    span {
      display: none;
    }
  }
`

const Navbar = ({ isAuthenticated, username, onLogout }) => {
  const location = useLocation()

  // Animation variants
  const linkVariants = {
    hover: {
      scale: 1.05,
      y: -2,
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  }

  const userInfoVariants = {
    initial: { opacity: 0, x: 20 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
        delay: 0.2,
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 4px 12px rgba(79, 141, 251, 0.2)",
    },
  }

  return (
    <NavbarContainer>
      <Logo to="/">
        <SynthlinxLogo size="small" />
      </Logo>

      <NavLinks>
        <NavLink
          to="/"
          className={location.pathname === "/" ? "active" : ""}
          variants={linkVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <HomeIcon fontSize="small" />
          <span>Home</span>
        </NavLink>

        {isAuthenticated && (
          <>
            <NavLink
              to="/query"
              className={location.pathname === "/query" ? "active" : ""}
              variants={linkVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <SearchIcon fontSize="small" />
              <span>Query</span>
            </NavLink>

            <NavLink
              to="/history"
              className={location.pathname === "/history" ? "active" : ""}
              variants={linkVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <HistoryIcon fontSize="small" />
              <span>History</span>
            </NavLink>
          </>
        )}
      </NavLinks>

      <UserSection>
        {isAuthenticated ? (
          <>
            <UserInfo variants={userInfoVariants} initial="initial" animate="animate" whileHover="hover">
              <PersonIcon fontSize="small" />
              <span>{username}</span>
            </UserInfo>

            <LogoutButton onClick={onLogout} variants={linkVariants} whileHover="hover" whileTap="tap">
              <LogoutIcon fontSize="small" />
              <span>Logout</span>
            </LogoutButton>
          </>
        ) : (
          <NavLink
            to="/login"
            className={location.pathname === "/login" ? "active" : ""}
            variants={linkVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <span>Login</span>
          </NavLink>
        )}
      </UserSection>
    </NavbarContainer>
  )
}

export default Navbar

